import React from 'react';
import { useTranslation } from 'react-i18next';
import { HRSummaryRichPayload, normalizeHrTimeline, toPositiveNumber } from './contentCardUtils';
import './richContext.scss';

type Props = {
  p: HRSummaryRichPayload;
};

const HRSummaryContent: React.FC<Props> = ({ p }) => {
  const { t } = useTranslation();
  const summary = p.heart_rate_summary;
  const avgHr = toPositiveNumber(summary?.avg_hr_bpm ?? p.avg_hr_bpm);
  const minHr = toPositiveNumber(summary?.min_hr_bpm ?? p.min_hr_bpm);
  const maxHr = toPositiveNumber(summary?.max_hr_bpm ?? p.max_hr_bpm);
  const restingHr = toPositiveNumber(summary?.resting_hr_bpm ?? p.resting_hr);
  const avgHrvRmssd = toPositiveNumber(summary?.avg_hrv_rmssd ?? p.avg_hrv_rmssd);
  const avgHrvSdnn = toPositiveNumber(summary?.avg_hrv_sdnn ?? p.avg_hrv_sdnn);

  const timeline = normalizeHrTimeline(p.timeline) ?? normalizeHrTimeline(p.chart_data) ?? [];
  const values = timeline
    .map((point) => point?.value)
    .filter((value): value is number => typeof value === 'number');
  const minTimeline = values.length ? Math.min(...values) : undefined;
  const maxTimeline = values.length ? Math.max(...values) : undefined;

  return (
    <div className="rc-content-body">
      <div className="rc-stat-row">
        {typeof avgHr === 'number' && (
          <div className="rc-stat-item">
            <span className="rc-stat-value">{Math.round(avgHr)}</span>
            <span className="rc-stat-label">{t('task.richContext.hr.avgBpm')}</span>
          </div>
        )}
        {typeof minHr === 'number' && (
          <div className="rc-stat-item">
            <span className="rc-stat-value">{Math.round(minHr)}</span>
            <span className="rc-stat-label">{t('task.richContext.hr.min')}</span>
          </div>
        )}
        {typeof maxHr === 'number' && (
          <div className="rc-stat-item">
            <span className="rc-stat-value">{Math.round(maxHr)}</span>
            <span className="rc-stat-label">{t('task.richContext.hr.max')}</span>
          </div>
        )}
      </div>
      {(typeof restingHr === 'number' ||
        typeof avgHrvRmssd === 'number' ||
        typeof avgHrvSdnn === 'number') && (
        <div className="rc-pill-row">
          {typeof restingHr === 'number' && (
            <div className="rc-tag-pill">
              <span className="rc-tag-pill-text">
                {t('task.richContext.hr.restingHr', { value: Math.round(restingHr) })}
              </span>
            </div>
          )}
          {typeof avgHrvRmssd === 'number' && (
            <div className="rc-tag-pill">
              <span className="rc-tag-pill-text">
                {t('task.richContext.hr.avgHrvRmssd', { value: Math.round(avgHrvRmssd) })}
              </span>
            </div>
          )}
          {typeof avgHrvSdnn === 'number' && (
            <div className="rc-tag-pill">
              <span className="rc-tag-pill-text">
                {t('task.richContext.hr.avgHrvSdnn', { value: Math.round(avgHrvSdnn) })}
              </span>
            </div>
          )}
        </div>
      )}
      {timeline.length > 0 && (
        <div className="rc-hr-timeline-row">
          {timeline.slice(0, 24).map((point, i) => {
            const value = typeof point.value === 'number' ? point.value : (minTimeline ?? 0);
            const min = minTimeline ?? value;
            const max = maxTimeline ?? value + 1;
            const ratio = Math.max(0.12, (value - min) / Math.max(max - min, 1));
            return (
              <span
                key={`${point.timestamp}_${i}`}
                className="rc-hr-bar"
                style={{ height: 10 + ratio * 34 }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HRSummaryContent;
